import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useObservable } from '@ngneat/react-rxjs'

import { materialsService } from '../../store/materials'
import { catalogsService } from '../../store/catalogs'
import { sessionQuery, sessionService } from '../../store/session'
import { materialsQuery } from '../../store/materials'
import useSnackbar from '../../hooks/useSnackbar.hooks'
import useRoute from '../../hooks/useRoute.hooks'

import { ManageMaterial, MaterialType } from '../../models/materials.models'
import { CatalogLight, Catalog, NetworkStatus } from '../../models/catalogs.models'
import { CatalogsApi } from '../../api/catalogs.api'
import { Route, Mode } from '../../models/commons.models'
import Constants from '../../constants'

import ModalFormMaterial from './ModalForm.material'
import ModalExport from './ModalExport.material'
import ModalImport from './ModalImport.material'
import LoaderOverlay from '../layout/LoaderOverlay.layout'

export type ModalType = 'add' | 'import' | 'export'

interface MaterialModalsManagerProps {
  catalog?: CatalogLight | Catalog
  materialType: MaterialType
  checkError?: 'pem' | 'd' | undefined
  modal: ModalType | undefined
  onClose: () => void
  onSuccess?: (modalType: ModalType) => void
  useImperials?: boolean
  showCerfa?: boolean
  onUpdateList?: (value: boolean | 'materialPemTable' | 'materialDTable') => void
}

const MaterialModalsManager: React.FC<MaterialModalsManagerProps> = ({
  catalog,
  materialType,
  checkError,
  modal,
  onClose,
  onSuccess,
  useImperials: useImperialsProps,
  showCerfa,
  onUpdateList,
}) => {
  const { t } = useTranslation()
  const show = useSnackbar()
  const { goTo, isAtRoute, getPath } = useRoute()
  const [useImperialsSession] = useObservable(sessionQuery.useImperials)
  const [materials] = useObservable(materialsQuery.materials)
  const useImperials = useImperialsProps ?? useImperialsSession
  const [loading, setLoading] = useState<boolean>(false)

  const isAdmin = Constants.mode === Mode.admin
  const catalogId = catalog?._id

  const isLocal = useMemo(() => Constants.getIsLocal(), [])
  const isSync = useMemo(
    () =>
      !isLocal ||
      (catalog?.networkStatus === NetworkStatus.sync &&
        catalog.lastSyncAt &&
        (!catalog?.localUpdatedAt || catalog?.lastSyncAt >= catalog?.localUpdatedAt)),
    [catalog, isLocal],
  )

  const checkSync = useCallback(
    async (callback: () => Promise<any>) => {
      if (!isSync) {
        throw new Error('NEED_SYNC')
      } else {
        return await callback()
      }
    },
    [isSync],
  )

  // Import a new material
  const importMaterials = useCallback(
    async ({ materials }: { materials: ManageMaterial[] }) => {
      if (!catalog) return

      setLoading(true)
      try {
        for (let i = 0; i < materials.length; i++) {
          await materialsService.createMaterial(catalog, materials[i])
        }

        onSuccess?.('import')
        show(t('catalogs:actions.import.success'))
      } catch (err: any) {
        show(err)
      } finally {
        setLoading(false)
      }
    },
    [catalog, onSuccess, show, t],
  )

  // Create a new material
  const createMaterial = useCallback(
    async (material: ManageMaterial) => {
      if (!catalog) return

      setLoading(true)
      try {
        await materialsService.createMaterial(catalog, material)

        onSuccess?.('add')
        show(t(`materials:actions.add${materialType}.success`))
      } catch (err: any) {
        show(err)
      } finally {
        setLoading(false)
      }
    },
    [catalog, materialType, onSuccess, show, t],
  )

  const getCatalogExport = useCallback(
    async (format: CatalogsApi.CatalogExportType) => {
      setLoading(true)
      try {
        const checkCerfa = async (type: 'pem' | 'd'): Promise<boolean> => {
          onUpdateList?.(false)
          // transfered materials
          const { data } = await materialsService.getMaterials({
            owned: !isAdmin,
            originalCatalog: catalogId,
            disablePaginate: true,
          })
          const allMaterials = materials.concat(data)
          const parsedMaterials = materialsService.parseList(type, allMaterials)
          if (parsedMaterials.find((material) => !!material.error)) {
            show(t('errors:exportMissingData'), 'warning')
            setLoading(false)
            const modeKey = type === 'pem' ? 'materialPemTable' : 'materialDTable'
            if (catalogId) {
              if (isAtRoute(Route.workspaceCatalogMaterials)) {
                if (
                  sessionService.getModeKey(
                    getPath({ route: Route.workspaceCatalogMaterials, catalogId }),
                  ) !== modeKey
                ) {
                  onUpdateList?.(modeKey)
                }
              } else {
                sessionService.setModeKey(
                  getPath({ route: Route.workspaceCatalogMaterials, catalogId }),
                  modeKey,
                )
                goTo({ route: Route.workspaceCatalogMaterials, catalogId })
              }
            }
            return false
          }
          return true
        }

        if (
          (format.type === 'cerfa' || format.type === 'exportPem') &&
          !(await checkCerfa('pem'))
        ) {
          return
        }
        if ((format.type === 'cerfa' || format.type === 'exportD') && !(await checkCerfa('d'))) {
          return
        }
        await checkSync(async () => {
          await catalogsService.downloadDiagnostic(catalog as Catalog, format)
        })
        onSuccess?.('export')
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    },
    [
      catalog,
      catalogId,
      materials,
      isAdmin,
      onUpdateList,
      show,
      t,
      isAtRoute,
      getPath,
      goTo,
      onSuccess,
      checkSync,
    ],
  )

  if (!modal || !catalog) return null

  return (
    <>
      {loading && <LoaderOverlay />}

      {modal === 'import' && (
        <ModalImport
          showCerfa={showCerfa ?? !!catalog?.enableCerfa}
          catalogType={catalog.type}
          title={t(`catalogs:actions.import.label`)}
          type={materialType}
          useImperials={useImperials}
          submitLabel={t(`global:actions.send`)}
          onClose={onClose}
          onSubmit={importMaterials}
          onSuccess={() => onSuccess?.('import')}
        />
      )}

      {modal === 'export' && (
        <ModalExport
          showCerfa={!!catalog?.enableCerfa}
          type={materialType}
          onClose={onClose}
          onSubmit={getCatalogExport}
        />
      )}

      {modal === 'add' && (
        <ModalFormMaterial
          checkError={checkError}
          showCerfa={!!catalog?.enableCerfa}
          catalogType={catalog.type}
          material={{
            catalog: catalog._id,
            type: materialType,
          }}
          useImperials={useImperials}
          onClose={onClose}
          onSubmit={createMaterial}
          submitLabel={t(`global:actions.finish`)}
          title={t(`materials:actions.add${materialType}.label`)}
          onSuccess={() => onSuccess?.('add')}
        />
      )}
    </>
  )
}

export default MaterialModalsManager
